// @ts-check

/**
 * @file Resolves backend URLs and performs JSON API requests.
 */

import {Platform} from "react-native"

import secrets from "../config/secrets"
import {expoPublicEnv} from "./expo-public-env"
import {optionalRouteParam} from "./route-params"

const DEFAULT_BACKEND_PORT = 4182

/**
 * @typedef {object} JsonRequestOptions
 * @property {string} [body] Serialized request body.
 * @property {Record<string, string>} [headers] Headers merged into the request.
 * @property {string} [method] HTTP method to use.
 */

/**
 * Resolves the HTTP API base URL from query params, secrets, or the browser URL.
 * @param {Record<string, string | string[] | undefined>} params Current route params.
 * @returns {string} HTTP origin and optional base path for API requests.
 * @throws {Error} Throws when no API URL source is available.
 */
export function resolveApiBaseUrl(params) {
  const backendHost = optionalRouteParam(params, "backendHost", "Backend host")
  const backendPort = optionalRouteParam(params, "backendPort", "Backend port")
  const backendProtocol = optionalRouteParam(params, "backendProtocol", "Backend protocol") ?? "http"
  const expoPublicApiBaseUrl = resolveExpoPublicApiBaseUrl()
  const nativeDevelopmentApiBaseUrl = resolveNativeDevelopmentApiBaseUrl()

  if (backendHost && backendPort) {
    return `${backendProtocol}://${backendHost}:${backendPort}`
  }

  if (expoPublicApiBaseUrl) return expoPublicApiBaseUrl

  if (nativeDevelopmentApiBaseUrl) return nativeDevelopmentApiBaseUrl

  if (secrets.apiBaseUrl) return secrets.apiBaseUrl

  if (typeof window === "undefined" || !window.location) {
    throw new Error("Cannot resolve API base URL: no window.location and secrets.apiBaseUrl is not set")
  }

  return `${window.location.protocol}//${window.location.hostname}:${secrets.apiBasePort}`
}

/**
 * Resolves a backend URL explicitly baked into an Expo native build.
 * @returns {string | null} Configured backend URL, when present.
 */
function resolveExpoPublicApiBaseUrl() {
  const backendHost = expoPublicEnv("EXPO_PUBLIC_BACKEND_HOST")

  if (!backendHost) return null

  const backendPort = expoPublicEnv("EXPO_PUBLIC_BACKEND_PORT")
  const backendProtocol = expoPublicEnv("EXPO_PUBLIC_BACKEND_PROTOCOL") || "http"
  const portSuffix = backendPort ? `:${backendPort}` : ""

  return `${backendProtocol}://${backendHost}${portSuffix}`
}

/**
 * Resolves the native development backend URL.
 * @returns {string | null} Native development backend URL, when configured.
 */
function resolveNativeDevelopmentApiBaseUrl() {
  const adbIp = expoPublicEnv("EXPO_PUBLIC_ADB_IP") || process.env.ADB_IP
  const velociousEnv = expoPublicEnv("EXPO_PUBLIC_VELOCIOUS_ENV") || process.env.NODE_ENV || "development"

  if (Platform.OS === "web" || velociousEnv !== "development" || !adbIp) return null

  const backendPort = expoPublicEnv("EXPO_PUBLIC_BACKEND_PORT") || String(secrets.apiBasePort || DEFAULT_BACKEND_PORT)

  return `http://${adbIp}:${backendPort}`
}

/**
 * Resolves the websocket endpoint for the backend.
 * @param {Record<string, string | string[] | undefined>} params Current route params.
 * @returns {string} Connection URL for the realtime transport.
 */
export function resolveWebsocketUrl(params) {
  const apiBaseUrl = resolveApiBaseUrl(params)
  const baseOrigin = typeof window !== "undefined" && window.location ? window.location.origin : "http://127.0.0.1"
  const normalizedUrl = new URL(apiBaseUrl, baseOrigin)
  const websocketProtocol = normalizedUrl.protocol === "https:" ? "wss:" : "ws:"
  const normalizedPathname = normalizedUrl.pathname.replace(/\/+$/, "")
  const websocketPath = normalizedPathname ? `${normalizedPathname}/websocket` : "/websocket"

  return `${websocketProtocol}//${normalizedUrl.host}${websocketPath}`
}

/**
 * Sends a JSON request to the backend and parses the response.
 * @param {string} apiBaseUrl Base URL returned by `resolveApiBaseUrl()`.
 * @param {string} path Backend path to request.
 * @param {JsonRequestOptions} [requestOptions] Fetch options for the request.
 * @returns {Promise<unknown>} Parsed response body.
 * @rejects {Error} Rejects when the request times out or the backend returns an error.
 */
export async function requestJson(apiBaseUrl, path, requestOptions = {}) {
  const url = `${apiBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 15000)

  let response

  try {
    response = await fetch(url, {
      ...requestOptions,
      credentials: "include",
      headers: {
        accept: "application/json",
        ...(requestOptions.body ? {"content-type": "application/json"} : {}),
        ...(requestOptions.headers || {})
      },
      signal: controller.signal
    })
  } catch (error) {
    clearTimeout(timeoutId)

    if (expoPublicEnv("EXPO_PUBLIC_SYSTEM_TEST") === "true") {
      console.error("System-test backend request failed:", url, error)
    }

    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(`Request to ${url} timed out after 15 seconds`)
    }

    throw error
  }

  clearTimeout(timeoutId)

  const responseText = await response.text()
  const responseJson = responseText ? JSON.parse(responseText) : null

  if (!response.ok) {
    throw new Error(responseJson?.error || `Request failed with status ${response.status}`)
  }

  return responseJson
}
